import { useCallback, useEffect, useMemo, useState } from "react";
import { FormAlert, TableKit } from "@NP-Webber/crm-kit";
import {
  financeApi,
  formatShekels,
  type LedgerMovement,
  type LedgerMovementKind,
} from "../../api/financeApi";
import { toClientApiError } from "../../api/httpClient";

export type CustomerLedgerPanelProps = {
  customerId: string;
};

const KIND_LABELS: Record<LedgerMovementKind, string> = {
  charge: "חיוב",
  payment: "תשלום",
  expense_allocation: "הקצאת הוצאה",
};

function sourceLabel(row: LedgerMovement): string {
  if (row.paymentId) {
    return "תשלום";
  }
  if (row.subscriptionId) {
    return "מנוי";
  }
  if (row.expenseId) {
    return "הוצאה";
  }
  return "—";
}

function formatOccurredAt(value: string): string {
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return value;
  }
  return date.toLocaleString("he-IL", {
    dateStyle: "short",
    timeStyle: "short",
  });
}

export function CustomerLedgerPanel({ customerId }: CustomerLedgerPanelProps) {
  const [rows, setRows] = useState<LedgerMovement[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const items = await financeApi.listMovements(customerId);
      setRows(items);
    } catch (err) {
      setError(toClientApiError(err).message);
    } finally {
      setLoading(false);
    }
  }, [customerId]);

  useEffect(() => {
    void load();
  }, [load]);

  const columns = useMemo(
    () => [
      {
        key: "occurredAt",
        title: "תאריך",
        sortable: true,
        render: (_value: unknown, row: Record<string, unknown>) =>
          formatOccurredAt(String(row.occurredAt ?? "")),
      },
      {
        key: "kind",
        title: "סוג",
        sortable: true,
        filterable: true,
        render: (_value: unknown, row: Record<string, unknown>) => {
          const kind = row.kind as LedgerMovementKind;
          return KIND_LABELS[kind] ?? String(row.kind ?? "");
        },
      },
      {
        key: "amountAgorot",
        title: "סכום (₪)",
        sortable: true,
        render: (_value: unknown, row: Record<string, unknown>) => {
          const amount = Number(row.amountAgorot ?? 0);
          const sign = amount > 0 ? "+" : "";
          return `${sign}${formatShekels(amount)}`;
        },
      },
      {
        key: "currency",
        title: "מטבע",
      },
      {
        key: "source",
        title: "מסמך מקור",
        render: (_value: unknown, row: Record<string, unknown>) =>
          sourceLabel(row as unknown as LedgerMovement),
      },
      {
        key: "note",
        title: "הערה",
        render: (_value: unknown, row: Record<string, unknown>) =>
          (row.note as string | null) || "—",
      },
    ],
    [],
  );

  const tableData = useMemo(
    () =>
      rows.map((row) => ({
        ...row,
        source: sourceLabel(row),
      })) as unknown as Array<Record<string, unknown>>,
    [rows],
  );

  return (
    <div className="related-panel">
      {error ? <FormAlert severity="error">{error}</FormAlert> : null}
      <p className="related-panel__hint">
        עובר ושב — תנועות פנקס תפעוליות. יתרה נגזרת מתנועות בלבד (לא לעריכה ידנית).
      </p>
      <div className="page-section__table-surface">
        <TableKit
          columns={columns}
          data={tableData}
          loading={loading}
          clientSideMode
          emptyMessage="אין תנועות עובר ושב עדיין."
          exportFileName="customer-ledger"
          showExport
          showFilters
          showColumnPicker
        />
      </div>
    </div>
  );
}
